/**
 * CloRiskReturnBlock — renders 1y/2y/3y annualized total return,
 * volatility, and Sharpe ratio for a CLO ETF, with a side-by-side
 * comparison against its sister fund (AAA senior ↔ BBB/B mezzanine)
 * when a pair is known, plus a plain-English breakdown of what each
 * number means and how it was computed.
 *
 * Used by:
 *   - CloExplainerCard (stock modal Insights tab)
 *   - CloEtfSection    (public SEO stock page)
 *
 * All numbers come from `/api/stocks/{ticker}/clo-metrics` — Massive
 * daily bars + our own dividend cache reinvested into a total-return
 * series, then annualized against the current 3-year US Treasury.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, TrendingUp, Scale, Calculator, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Family label mapping — kept in sync with backend `tranche_family`.
const FAMILY_LABEL = { aaa: 'AAA senior', mezz: 'BBB / B mezzanine', mixed: 'Mixed / broad' };
const FAMILY_TONE = {
  aaa: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800',
  mezz: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
  mixed: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800',
};

// Sharpe reading — kept simple, matches how Morningstar labels them.
function sharpeVerdict(s) {
  if (s == null) return null;
  if (s >= 1.5) return { label: 'Excellent', tone: 'text-emerald-600 dark:text-emerald-400' };
  if (s >= 1) return { label: 'Good', tone: 'text-emerald-600 dark:text-emerald-400' };
  if (s >= 0.5) return { label: 'Adequate', tone: 'text-amber-600 dark:text-amber-400' };
  if (s >= 0) return { label: 'Weak', tone: 'text-amber-600 dark:text-amber-400' };
  return { label: 'Negative', tone: 'text-rose-600 dark:text-rose-400' };
}

function useCloMetrics(ticker) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true); setData(null);
    axios.get(`${API}/stocks/${ticker}/clo-metrics`, { timeout: 15000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ enabled: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);
  return { data, loading };
}

function MetricCell({ label, value, suffix = '%', tone, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
      <span className={`text-sm font-mono font-bold tabular-nums ${tone || 'text-foreground'}`}>
        {value == null ? '—' : `${value}${suffix}`}
      </span>
    </div>
  );
}

function WindowRow({ w, isSister, ticker }) {
  const verdict = sharpeVerdict(w?.sharpe);
  return (
    <tr className="border-t border-border/40" data-testid={`clo-metrics-row-${ticker}-${w.years}y`}>
      <td className="py-1.5 pr-2 font-semibold text-xs">
        {w.years}-year
        <span className="ml-1 text-[10px] text-muted-foreground font-normal">({w.n_trading_days}d)</span>
      </td>
      <td className="py-1.5 px-2 font-mono text-xs tabular-nums text-emerald-700 dark:text-emerald-400">
        {w.cagr_pct != null ? `${w.cagr_pct.toFixed(2)}%` : '—'}
      </td>
      <td className="py-1.5 px-2 font-mono text-xs tabular-nums text-amber-700 dark:text-amber-400">
        {w.vol_pct != null ? `${w.vol_pct.toFixed(2)}%` : '—'}
      </td>
      <td className="py-1.5 pl-2 font-mono text-xs tabular-nums">
        <span className={verdict?.tone || ''}>{w.sharpe != null ? w.sharpe.toFixed(2) : '—'}</span>
        {verdict && !isSister && <span className={`ml-1 text-[9px] font-semibold ${verdict.tone}`}>{verdict.label}</span>}
      </td>
    </tr>
  );
}

function MetricsTable({ metrics, isSister = false, testidPrefix }) {
  if (!metrics?.windows?.length) return null;
  return (
    <table className="w-full text-left" data-testid={`${testidPrefix}-table`}>
      <thead>
        <tr>
          <th className="pb-1 pr-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Window</th>
          <th className="pb-1 px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total return (ann.)</th>
          <th className="pb-1 px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Volatility</th>
          <th className="pb-1 pl-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Sharpe</th>
        </tr>
      </thead>
      <tbody>
        {metrics.windows.map((w) => (
          <WindowRow key={w.years} w={w} isSister={isSister} ticker={metrics.ticker} />
        ))}
      </tbody>
    </table>
  );
}

export default function CloRiskReturnBlock({ ticker, compact = false }) {
  const { data, loading } = useCloMetrics(ticker);
  if (loading) {
    return (
      <div className="mt-3 p-3 rounded border border-border/50 bg-muted/20" data-testid="clo-metrics-loading">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-[11px] text-muted-foreground">Computing 3-year risk & return…</span>
        </div>
      </div>
    );
  }
  if (!data?.enabled) {
    if (data?.reason === 'insufficient_history') {
      return (
        <div className="mt-3 p-2 rounded border border-border/50 bg-muted/20 text-[11px] text-muted-foreground" data-testid="clo-metrics-unavailable">
          Risk & return will appear once {ticker} has at least a year of price history.
        </div>
      );
    }
    return null;
  }
  const { self, sister } = data;
  const selfFamily = self.family;
  const sisterFamily = sister?.family;
  const rf = self.risk_free_rate_pct;
  const w3 = self.windows.find((x) => x.years === 3) || self.windows[0];
  const sw3 = sister?.windows?.find((x) => x.years === 3) || sister?.windows?.[0];

  return (
    <div className="mt-3 rounded-lg border border-sky-200/70 dark:border-sky-800/50 bg-white/70 dark:bg-slate-950/40 p-3" data-testid="clo-risk-return-block">
      <div className="flex items-baseline gap-2 flex-wrap">
        <Activity className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
        <span className="text-sm font-bold text-sky-800 dark:text-sky-300">Risk &amp; Return</span>
        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${FAMILY_TONE[selfFamily]}`}>{FAMILY_LABEL[selfFamily]}</span>
        <span className="text-[10px] text-muted-foreground ml-auto">Total return with dividends reinvested · Sharpe vs {rf?.toFixed(2)}% 3-yr Treasury</span>
      </div>

      {/* Ticker's own numbers */}
      <div className="mt-2 rounded border border-border/40 bg-background/60 p-2">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <span className="text-[11px] font-bold font-mono">{self.ticker}</span>
          <span className="text-[10px] text-muted-foreground">{self.n_dividends_reinvested} dividend{self.n_dividends_reinvested === 1 ? '' : 's'} reinvested</span>
        </div>
        <MetricsTable metrics={self} testidPrefix="clo-metrics-self" />
      </div>

      {/* Sister-fund side-by-side */}
      {sister && (
        <div className="mt-2 rounded border border-border/40 bg-background/60 p-2" data-testid="clo-metrics-sister-block">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <span className="text-[11px] font-bold font-mono">{sister.ticker} · sister fund</span>
            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${FAMILY_TONE[sisterFamily]}`}>{FAMILY_LABEL[sisterFamily]}</span>
          </div>
          <MetricsTable metrics={sister} isSister testidPrefix="clo-metrics-sister" />
          {w3 && sw3 && !compact && (
            <p className="text-[11px] leading-relaxed text-muted-foreground mt-1.5">
              <TrendingUp className="w-3 h-3 inline mr-1 text-emerald-500" />
              {selfFamily === 'mezz'
                ? <>Over the trailing 3 years, <strong className="text-foreground">{self.ticker}</strong> earned <strong className="text-emerald-700 dark:text-emerald-400">{(w3.cagr_pct - sw3.cagr_pct).toFixed(2)}pt more</strong> per year than its AAA-senior sister <strong className="text-foreground">{sister.ticker}</strong> — the &ldquo;yield pick-up&rdquo; for moving down the waterfall — but at <strong className="text-amber-700 dark:text-amber-400">{(w3.vol_pct / sw3.vol_pct).toFixed(1)}× the volatility</strong>. Sharpe ratios show which delivered that return more efficiently.</>
                : <>Over the trailing 3 years, <strong className="text-foreground">{self.ticker}</strong> earned <strong>{w3.cagr_pct.toFixed(2)}%</strong> annualized with just <strong className="text-emerald-700 dark:text-emerald-400">{w3.vol_pct.toFixed(2)}% volatility</strong> — its mezzanine sister <strong className="text-foreground">{sister.ticker}</strong> pushed {(sw3.cagr_pct - w3.cagr_pct).toFixed(2)}pt higher return but at {(sw3.vol_pct / w3.vol_pct).toFixed(1)}× the volatility. Compare Sharpe ratios to see which paid for its risk.</>}
            </p>
          )}
        </div>
      )}

      {!compact && (
        <>
          {/* Rate-cut narrative — the two-sided story */}
          <div className="mt-2 p-2 rounded border border-amber-200/70 dark:border-amber-800/50 bg-amber-50/60 dark:bg-amber-950/20 text-[11px] leading-relaxed" data-testid="clo-rate-narrative">
            <div className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1"><Scale className="w-3.5 h-3.5" /> How Fed rate cuts affect this fund</div>
            <ul className="mt-1 space-y-1 text-amber-900/90 dark:text-amber-200/90 list-disc pl-4">
              <li><strong>Income compression:</strong> CLO loans pay SOFR + spread, so each Fed cut trims the monthly distribution within a quarter. {self.ticker}&apos;s coupon reset schedule is quarterly.</li>
              <li><strong>Credit cushion:</strong> lower rates ease the interest burden on the leveraged corporate borrowers inside the pool — fewer defaults means NAV stays steady.
                {selfFamily === 'mezz' && ' This matters more for BBB-B tranches like this one, which absorb losses before AAA.'}
              </li>
              <li><strong>No duration lift:</strong> unlike fixed-rate bond funds, floating-rate CLOs won&apos;t see capital gains when rates fall — price stays flat, yield falls.</li>
            </ul>
          </div>

          {/* Formula panel — collapsible only via the details tag */}
          <details className="mt-2 rounded border border-border/40 bg-muted/30 text-[11px]" data-testid="clo-metrics-math">
            <summary className="cursor-pointer px-2 py-1 font-semibold flex items-center gap-1 select-none">
              <Calculator className="w-3.5 h-3.5" /> Show the math
            </summary>
            <div className="px-2 pb-2 pt-1 space-y-2 leading-relaxed">
              <div>
                <div className="font-semibold text-foreground">Annualized total return (CAGR)</div>
                <code className="block mt-0.5 px-2 py-1 rounded bg-background/70 border border-border/40 font-mono text-[10.5px]">
                  ((End / Start) ^ (1 / years)) − 1
                </code>
                <p className="text-muted-foreground mt-0.5">End &amp; Start are prices in a total-return series — every dividend is reinvested on its ex-date, so the number includes both share-price change and reinvested payouts.</p>
              </div>
              <div>
                <div className="font-semibold text-foreground">Annualized volatility (standard deviation)</div>
                <code className="block mt-0.5 px-2 py-1 rounded bg-background/70 border border-border/40 font-mono text-[10.5px]">
                  σ_annual = σ_daily × √252
                </code>
                <p className="text-muted-foreground mt-0.5">σ_daily is the sample standard deviation of daily log returns of the total-return series. Multiplying by √252 (trading days per year) scales it to an annual figure.</p>
              </div>
              <div>
                <div className="font-semibold text-foreground">Sharpe ratio</div>
                <code className="block mt-0.5 px-2 py-1 rounded bg-background/70 border border-border/40 font-mono text-[10.5px]">
                  (CAGR − Rf) / σ_annual
                </code>
                <p className="text-muted-foreground mt-0.5">
                  Rf is the current 3-year US Treasury constant maturity ({rf?.toFixed(2)}% today).
                  Above 1 = each 1% of volatility bought &gt;1% of excess return. Above 1.5 = excellent risk efficiency.
                </p>
              </div>
              <div className="pt-1 border-t border-border/40 text-muted-foreground flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Prices come from Massive.com split-adjusted daily closes; dividends from our own cache. Sharpe uses the live 3-year Treasury yield, not a long-term average — published fund fact-sheets may use a slightly different Rf and horizon and land within about 0.2 of these numbers.</span>
              </div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}
